import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {LayoutSidebar} from '../../../Layout';
import {PromptLibraryApi} from '../../../api/promptLibraryApi';
import {APIClient} from '../../../utils/APIClient';
import {LocalStorage} from '../../../utils/LocalStorage';
import {PROJECT_INSTRUCTION, promptAskPhrase} from '../../../utils/promptAskPhrase';
import {askPhraseToastContent} from './askPhraseToast';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {FaIcon} from '../../FaIcon';
import {Spinner} from '../../Spinner';
import {copyText} from './copyText';
import {DashboPromptsTab} from './DashboPromptsTab';
import {EDITOR_DUPLICATE, EDITOR_EDIT, EDITOR_NEW, PromptEditorModal} from './editor/PromptEditorModal';
import {ALL_CLIENTS, groupOwnPrompts, matchesClient, sortClients} from './groupOwnPrompts';
import {exampleAskTitle, HowItWorks} from './HowItWorks';
import {
    deactivateOwn, isAck, isLibraryPayload, isPromptPayload, markAdapted, nextRating, patchDashboSocial,
    patchOwn, restoreOwn, toOwnRow, upsertOwn,
} from './libraryState';
import {OwnPromptsTab} from './OwnPromptsTab';
import {PrivacyChip} from './PrivacyChip';
import {MENU_COPY_TEXT, MENU_DEACTIVATE, MENU_DUPLICATE, MENU_EDIT, MENU_VIEW_TEXT} from './PromptRowMenu';
import {PromptTextModal} from './PromptTextModal';
import {readPrefs, writePrefs} from './promptsScreenPrefs';
import {dismissUndoToast, showUndoToast} from './promptUndoToast';
import {SetupAiCard} from './SetupAiCard';

export const TAB_OWN = 'own';
export const TAB_DASHBO = 'dashbo';
const BASE_PATH = '/agency/prompts';
const HIGHLIGHT_MS = 2000;
const RAW = {interpolation: {escapeValue: false}};
// La banda «Usa tu IA» del cliente lee la biblioteca con `{cache: true}` (TTL de 30 s): después de
// cambiar algo acá, volver a la banda con «atrás» mostraba el prompt viejo.
const LIBRARY_CACHE_PATTERN = 'agent/prompt-library/';

// `my_ai` es opcional: un backend que todavía no lo manda deja el paso «Conectar tu IA» para marcar a
// mano, en vez de afirmar algo que no sabe.
function withDefaults(payload) {
    return {
        ...payload,
        agency: payload.agency || null,
        clients: payload.clients || [],
        inactive_own: payload.inactive_own || [],
        dashbo: payload.dashbo || [],
        my_ai: payload.my_ai || null,
    };
}

// El cliente del filtro, para abrir «Nuevo prompt» ya en «Un cliente: <ese>». Sólo si está activo:
// `create/` rechaza con 404 un cliente inactivo aunque el payload lo liste.
function activeFilterClientId(library, clientFilter) {
    if (clientFilter === ALL_CLIENTS) return null;
    const client = (library.clients || []).find((item) => String(item.id) === String(clientFilter));
    return client && client.is_active !== false ? clientFilter : null;
}

/**
 * Pantalla «Prompts» de la agencia (`/agency/prompts` y `/agency/prompts/dashbo`): la biblioteca
 * propia —lo que la IA del equipo sabe hacer a la manera de la agencia— y, en otra solapa, los
 * sugeridos por Dashbo para adaptar. Plan: `~/.claude/plans/2026-09-25-galeria-prompts/`.
 *
 * Separa los tres momentos que antes tenían el mismo peso (rediseño «A», 04/10): entender qué es
 * («Cómo funciona», se oculta), preparar la IA («Prepara tu IA», se achica cuando está lista) y usar
 * los prompts (la lista, con la frase a la vista antes de copiarla).
 *
 * Una sola llamada arma la pantalla (`GET agent/prompt-library/agency/`) y todo el filtrado es
 * client-side. Después de cada cambio la lista se actualiza en el lugar y se recarga en silencio,
 * para traer los campos que sólo arma ese endpoint (autor, uso por la IA, versión). Una recarga
 * silenciosa que falla no toca lo que ya se ve.
 */
export function AgencyPromptsContent() {
    const {t} = useTranslation();
    const params = useParams() || {};
    const navigate = useNavigate();
    const location = useLocation() || {};
    const activeTab = params.tab === TAB_DASHBO ? TAB_DASHBO : TAB_OWN;

    const [library, setLibrary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [now, setNow] = useState(() => new Date());
    const [search, setSearch] = useState('');
    const [clientFilter, setClientFilter] = useState(ALL_CLIENTS);
    const [editor, setEditor] = useState(null);
    const [textPrompt, setTextPrompt] = useState(null);
    const [highlightId, setHighlightId] = useState(null);
    const [pendingId, setPendingId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);
    const [adaptingId, setAdaptingId] = useState(null);
    const [prefs, setPrefs] = useState(() => readPrefs(LocalStorage.fetchUserId()));
    const howLinkRef = useRef(null);
    const focusHowLink = useRef(false);
    const mounted = useRef(true);
    const highlightTimer = useRef(null);
    const editorSeq = useRef(0);
    // Orden de las recargas: cada `load` lleva un número y sólo la ÚLTIMA pedida escribe la lista;
    // y una recarga que salió antes de un cambio local (un voto, el interruptor del portal, una baja)
    // no lo pisa: se descarta y se vuelve a pedir.
    const loadSeq = useRef(0);
    const mutationSeq = useRef(0);
    // Las acciones que terminan después de un cambio de solapa (el «Deshacer» del aviso, una respuesta
    // lenta) leen la solapa actual, no la que había cuando se dispararon.
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;

    const agencyName = library && library.agency ? library.agency.name : '';
    const ready = !loading && loadError === null && library !== null;

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(highlightTimer.current);
            // El «Deshacer» de una baja no sobrevive a la pantalla: su resultado ya no tendría dónde
            // mostrarse. La baja queda y se restaura desde «Desactivados».
            dismissUndoToast();
        };
    }, []);

    const load = useCallback(({silent = false} = {}) => {
        loadSeq.current += 1;
        const seq = loadSeq.current;
        const mutationsAtStart = mutationSeq.current;
        if (!silent) {
            setLoading(true);
            setLoadError(null);
        }
        const settle = (response) => {
            if (!mounted.current || seq !== loadSeq.current) return;
            if (silent && mutationsAtStart !== mutationSeq.current) {
                load({silent: true});
                return;
            }
            if (isLibraryPayload(response)) {
                setLibrary(withDefaults(response));
                setNow(new Date());
                setLoadError(null);
            } else if (!silent) {
                setLoadError('LOAD_FAILED');
            }
            if (!silent) setLoading(false);
        };
        return PromptLibraryApi.agencyLibrary().then(settle).catch(() => settle(null));
    }, []);

    // Todo cambio local de la lista pasa por acá: invalida las recargas en vuelo (ver `load`) y la
    // caché de la biblioteca que lee la banda.
    const mutate = (change) => {
        mutationSeq.current += 1;
        APIClient.clearCache(LIBRARY_CACHE_PATTERN);
        setLibrary(change);
    };

    useEffect(() => {
        load();
    }, [load]);

    // La fila recién creada, adaptada o restaurada se resalta 2 s y se trae a la vista. Corre también
    // cuando llega la recarga: después de un 409 de «Adaptar» la fila recién existe en ese momento.
    useEffect(() => {
        if (highlightId === null || activeTab !== TAB_OWN) return;
        const row = document.getElementById(`agp-row-${highlightId}`);
        if (row && row.scrollIntoView) row.scrollIntoView({block: 'center', behavior: 'smooth'});
    }, [highlightId, activeTab, library]);

    const updatePrefs = (patch) => {
        setPrefs((current) => {
            const next = {...current, ...patch};
            writePrefs(LocalStorage.fetchUserId(), next);
            return next;
        });
    };

    // Al ocultar «Cómo funciona» su botón desaparece: el foco pasa al link que lo vuelve a abrir, en
    // vez de caer a `<body>`.
    const hideHow = () => {
        focusHowLink.current = true;
        updatePrefs({howHidden: true});
    };
    useEffect(() => {
        if (!prefs.howHidden || !focusHowLink.current) return;
        focusHowLink.current = false;
        if (howLinkRef.current) howLinkRef.current.focus();
    }, [prefs.howHidden]);

    const highlight = (id) => {
        clearTimeout(highlightTimer.current);
        setHighlightId(id);
        highlightTimer.current = setTimeout(() => {
            if (mounted.current) setHighlightId(null);
        }, HIGHLIGHT_MS);
    };

    const pathFor = (tab) => `${tab === TAB_DASHBO ? `${BASE_PATH}/dashbo` : BASE_PATH}${location.search || ''}`;
    const goToTab = (tab) => {
        if (tab !== activeTabRef.current) navigate(pathFor(tab));
    };

    // Llevar a la solapa propia y resaltar una fila, limpiando lo que la pudiera esconder.
    const showOwnRow = (id) => {
        setSearch('');
        setClientFilter(ALL_CLIENTS);
        goToTab(TAB_OWN);
        highlight(id);
    };

    const pingUse = (prompt) => {
        PromptLibraryApi.used(prompt.id, prompt.client ? prompt.client.id : null);
    };

    // `showText`: el aviso de éxito muestra lo que se copió debajo del mensaje, y no se cierra al
    // hacerle clic — un clic selecciona la frase (`user-select: all`; review C-05).
    const copyAndTell = (text, doneKey, {prompt = null, onFail = null, showText = false} = {}) => {
        copyText(text)
            .then(() => {
                if (showText) toast.success(askPhraseToastContent(t(doneKey), text), {closeOnClick: false});
                else toast.success(t(doneKey));
                if (prompt) pingUse(prompt);
            })
            .catch(() => (onFail ? onFail() : toast.error(t('agency_prompts.copy_error'))));
    };

    // --- Solapa propia ------------------------------------------------------------------------

    // «Copiar pedido» NO avisa el uso (decisión de fede, 26/09, review C-fe-08): el uso real lo registra
    // el MCP cuando la IA trae el prompt, y pinguear también acá lo contaba dos veces. «Copiar el
    // texto» sí avisa: por ese camino el MCP no se entera.
    //
    // Si el navegador no deja copiar, el aviso no puede pedir «selecciona el texto» sin mostrarlo: la
    // frase del pedido no está escrita en la fila (va en el aviso, que no se cierra por clic para poder
    // seleccionarla), y el texto del prompt se abre en «Ver el texto».
    //
    // Un prompt de un cliente se pide CON su cliente (`client` es null en los de toda la agencia: acá
    // no hay un cliente al que fijarlos, y la IA pregunta para cuál). Por eso el aviso de éxito también
    // muestra la frase: es la única forma de ver qué cliente viaja en el pedido.
    const handleCopyAsk = (prompt) => {
        const phrase = promptAskPhrase(prompt.title, prompt.client);
        copyAndTell(phrase, 'agency_prompts.copy_ask_done', {
            showText: true,
            onFail: () => toast.error(
                askPhraseToastContent(t('agency_prompts.copy_ask_error'), phrase),
                {autoClose: false, closeOnClick: false, draggable: false},
            ),
        });
    };
    const handleCopyText = (prompt) => copyAndTell(prompt.prompt_text || '', 'agency_prompts.menu_copy_text_done', {
        prompt,
        onFail: () => {
            toast.error(t('agency_prompts.copy_error'));
            setTextPrompt(prompt);
        },
    });

    const handleRestore = (prompt) => {
        setRestoringId(prompt.id);
        PromptLibraryApi.restore(prompt.id)
            .then((response) => {
                if (!mounted.current) return;
                setRestoringId(null);
                if (isPromptPayload(response)) {
                    mutate((current) => restoreOwn(current, prompt.id, response));
                    toast.success(t('agency_prompts.restored_toast'));
                    showOwnRow(prompt.id);
                    load({silent: true});
                    return;
                }
                // 409: es una copia de un prompt de Dashbo que ya tiene OTRA copia activa (se volvió
                // a adaptar después de desactivarla). Reintentar no va a andar nunca: se dice por qué
                // y se muestra la que está activa.
                if (response && response.http_status === 409) {
                    toast.warning(t('agency_prompts.restore_conflict'));
                    const activeCopyId = response.raw_body && response.raw_body.fork_id;
                    if (activeCopyId) showOwnRow(activeCopyId);
                    load({silent: true});
                    return;
                }
                toast.error(t('agency_prompts.restore_error'));
            })
            .catch(() => {
                if (!mounted.current) return;
                setRestoringId(null);
                toast.error(t('agency_prompts.restore_error'));
            });
    };

    // Sin modal de confirmación: la baja se puede deshacer 8 s desde el aviso y, después, desde
    // «Desactivados» al pie (README §1.1, caso 2).
    const handleDeactivate = (prompt) => {
        setPendingId(prompt.id);
        PromptLibraryApi.deactivate(prompt.id)
            .then((response) => {
                if (!mounted.current) return;
                setPendingId(null);
                if (!isAck(response)) {
                    toast.error(t('agency_prompts.deactivate_error'));
                    return;
                }
                mutate((current) => deactivateOwn(current, prompt.id));
                load({silent: true});
                showUndoToast({
                    message: t('agency_prompts.deactivated_toast'),
                    undoLabel: t('agency_prompts.undo'),
                    onUndo: () => handleRestore(prompt),
                    actionId: prompt.id,
                });
            })
            .catch(() => {
                if (!mounted.current) return;
                setPendingId(null);
                toast.error(t('agency_prompts.deactivate_error'));
            });
    };

    const handleRowAction = (action, prompt) => {
        if (action === MENU_EDIT) openEditor(EDITOR_EDIT, prompt);
        else if (action === MENU_DUPLICATE) openEditor(EDITOR_DUPLICATE, prompt);
        else if (action === MENU_VIEW_TEXT) setTextPrompt(prompt);
        else if (action === MENU_COPY_TEXT) handleCopyText(prompt);
        else if (action === MENU_DEACTIVATE) handleDeactivate(prompt);
    };

    // Optimista con vuelta atrás: el interruptor responde al instante y sólo rebota si el backend
    // rechazó el cambio.
    const handleTogglePortal = (prompt, visible) => {
        const previous = Boolean(prompt.portal_visible);
        mutate((current) => patchOwn(current, prompt.id, {portal_visible: visible}));
        const revert = () => {
            if (!mounted.current) return;
            mutate((current) => patchOwn(current, prompt.id, {portal_visible: previous}));
            toast.error(t('agency_prompts.portal_error'));
        };
        PromptLibraryApi.setPortalVisibility(prompt.id, visible)
            .then((response) => { if (!isAck(response)) revert(); })
            .catch(revert);
    };

    const handleCopyInstruction = () => copyAndTell(PROJECT_INSTRUCTION, 'agency_prompts.howto_copy_instruction_done');

    // --- Editor -------------------------------------------------------------------------------

    function openEditor(mode, prompt = null) {
        editorSeq.current += 1;
        setEditor({mode, prompt, key: `${mode}-${prompt ? prompt.id : 'new'}-${editorSeq.current}`});
    }

    const handleSaved = ({mode, data, clientId, portalVisible}) => {
        setEditor(null);
        if (mode === EDITOR_EDIT) {
            mutate((current) => {
                const previous = (current.own || []).find((prompt) => prompt.id === data.id) || null;
                return upsertOwn(current, toOwnRow(data, {previous}));
            });
        } else {
            const known = clientId !== null
                ? (library.clients || []).find((client) => String(client.id) === String(clientId))
                : null;
            const client = clientId !== null ? {id: clientId, name: known ? known.name : ''} : null;
            const row = toOwnRow({...data, portal_visible: portalVisible || Boolean(data.portal_visible)}, {client});
            mutate((current) => upsertOwn(current, row));
            // Si el filtro de la lista lo escondería, se limpia: el usuario tiene que verlo aparecer.
            if (!matchesClient(row, clientFilter)) setClientFilter(ALL_CLIENTS);
            setSearch('');
            goToTab(TAB_OWN);
            highlight(data.id);
        }
        load({silent: true});
    };

    // --- Solapa Dashbo ------------------------------------------------------------------------

    const handleAdapt = (base) => {
        setAdaptingId(base.id);
        PromptLibraryApi.fork(base.id, null)
            .then((response) => {
                if (!mounted.current) return;
                setAdaptingId(null);
                if (isPromptPayload(response)) {
                    mutate((current) => markAdapted(
                        upsertOwn(current, toOwnRow(response, {client: null})), base.id, response.id));
                    toast.success(t('agency_prompts.dashbo_adapted_toast', {agency: agencyName, ...RAW}));
                    showOwnRow(response.id);
                    load({silent: true});
                    return;
                }
                // 409: ya había una copia de la agencia (la hizo un colega después de que se cargó
                // la pantalla). Es el mismo resultado que adaptar: se avisa y se muestra esa.
                if (response && response.http_status === 409) {
                    toast.info(t('agency_prompts.dashbo_already_adapted', {agency: agencyName, ...RAW}));
                    const forkId = response.raw_body && response.raw_body.fork_id;
                    if (forkId) {
                        mutate((current) => markAdapted(current, base.id, forkId));
                        showOwnRow(forkId);
                    }
                    load({silent: true});
                    return;
                }
                toast.error(t('agency_prompts.dashbo_adapt_error'));
            })
            .catch(() => {
                if (!mounted.current) return;
                setAdaptingId(null);
                toast.error(t('agency_prompts.dashbo_adapt_error'));
            });
    };

    const handleRate = (prompt, clicked) => {
        const previous = prompt.social;
        const {value, social} = nextRating(prompt.social, clicked);
        mutate((current) => patchDashboSocial(current, prompt.id, social));
        const revert = () => {
            if (!mounted.current) return;
            mutate((current) => patchDashboSocial(current, prompt.id, previous));
            toast.error(t('agency_prompts.rate_error'));
        };
        PromptLibraryApi.rate(prompt.id, value)
            .then((response) => { if (!isAck(response)) revert(); })
            .catch(revert);
    };

    // --- Render -------------------------------------------------------------------------------

    const renderBody = () => {
        if (loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        if (!ready) {
            return (
                <LoadErrorState title={t('agency_prompts.load_error')}
                                text={t('agency_prompts.load_error_explanation')}
                                retryLabel={t('agency_prompts.load_error_retry')}
                                onRetry={() => load()}/>
            );
        }
        const clients = sortClients(library.clients);
        const firstGroup = groupOwnPrompts(library.own, library.clients)[0];
        const firstActiveClient = clients.find((client) => client.is_active !== false);
        return (
            <>
                {!prefs.howHidden && (
                    <HowItWorks agencyName={agencyName} onHide={hideHow}
                                exampleTitle={exampleAskTitle(firstGroup ? firstGroup.prompts[0].title : null,
                                    firstActiveClient ? firstActiveClient.name : null)}/>
                )}
                <nav className="agp-tabs" aria-label={t('agency_prompts.title')}>
                    <Link to={pathFor(TAB_OWN)} className={`agp-tabs__tab${activeTab === TAB_OWN ? ' -on' : ''}`}
                          aria-current={activeTab === TAB_OWN ? 'page' : undefined}>
                        <span className="agp-tabs__label">{t('agency_prompts.tab_own')}</span>
                        <span className="agp-tabs__count">{library.own.length}</span>
                    </Link>
                    <Link to={pathFor(TAB_DASHBO)} className={`agp-tabs__tab${activeTab === TAB_DASHBO ? ' -on' : ''}`}
                          aria-current={activeTab === TAB_DASHBO ? 'page' : undefined}>
                        <span className="agp-tabs__label">{t('agency_prompts.tab_dashbo')}</span>
                        <span className="agp-tabs__count">{library.dashbo.length}</span>
                    </Link>
                </nav>
                {activeTab === TAB_OWN ? (
                    <div className="agp-body">
                        <div className="agp-list">
                            <OwnPromptsTab library={library} agencyName={agencyName} now={now}
                                           search={search} clientFilter={clientFilter}
                                           onSearchChange={setSearch} onClientFilterChange={setClientFilter}
                                           highlightId={highlightId} pendingId={pendingId} restoringId={restoringId}
                                           adaptingId={adaptingId}
                                           onNew={() => openEditor(EDITOR_NEW)}
                                           onSeeDashbo={() => goToTab(TAB_DASHBO)}
                                           onCopyAsk={handleCopyAsk} onAction={handleRowAction}
                                           onTogglePortal={handleTogglePortal} onRestore={handleRestore}
                                           onAdapt={handleAdapt} onSeeAdapted={(base) => showOwnRow(base.adapted.id)}
                                           onView={setTextPrompt} onRate={handleRate}/>
                        </div>
                        <div className="agp-side">
                            <SetupAiCard connectedByBackend={Boolean(library.my_ai && library.my_ai.recently_used)}
                                         aiConnected={prefs.aiConnected} instructionPasted={prefs.instructionPasted}
                                         onConnectedChange={(value) => updatePrefs({aiConnected: value})}
                                         onPastedChange={(value) => updatePrefs({instructionPasted: value})}
                                         onCopyInstruction={handleCopyInstruction}/>
                            <p className="agp-tip">
                                <strong className="agp-tip__title">{t('agency_prompts.tip_title')}</strong>
                                {t('agency_prompts.tip_body')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <DashboPromptsTab prompts={library.dashbo} agencyName={agencyName} adaptingId={adaptingId}
                                      onAdapt={handleAdapt} onSeeAdapted={(base) => showOwnRow(base.adapted.id)}
                                      onView={setTextPrompt} onRate={handleRate}/>
                )}
            </>
        );
    };

    return (
        <section className="section-content agp-screen">
            <div className="agp-page">
                <header className="agp-head">
                    <div>
                        <div className="agp-head__title-row">
                            <h1 className="agp-head__title">{t('agency_prompts.title')}</h1>
                            {ready && <PrivacyChip agencyName={agencyName}/>}
                        </div>
                        {ready && (
                            <p className="agp-head__subtitle">
                                {t('agency_prompts.subtitle')}
                                {prefs.howHidden && (
                                    <>
                                        {' '}
                                        <button type="button" ref={howLinkRef} className="agp-head__how"
                                                onClick={() => updatePrefs({howHidden: false})}>
                                            {t('agency_prompts.how_show')}
                                        </button>
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                    {ready && (
                        <button type="button" className="agp-btn -primary" onClick={() => openEditor(EDITOR_NEW)}>
                            <FaIcon iconName="plus"/>{t('agency_prompts.new_prompt')}
                        </button>
                    )}
                </header>
                {renderBody()}
            </div>

            {editor && ready && (
                <PromptEditorModal key={editor.key} mode={editor.mode} prompt={editor.prompt}
                                   agencyName={agencyName} clients={sortClients(library.clients)}
                                   ownPrompts={library.own}
                                   defaultClientId={editor.mode === EDITOR_NEW ? activeFilterClientId(library, clientFilter) : null}
                                   onClose={() => setEditor(null)} onSaved={handleSaved}/>
            )}
            <PromptTextModal prompt={textPrompt} onClose={() => setTextPrompt(null)} onCopyText={handleCopyText}/>
        </section>
    );
}

/**
 * Shell de la ruta: `LayoutSidebar` bifurca solo entre el marco clásico y el de la 2.1, así que la
 * pantalla entra a los dos sin tocar nada más.
 */
export function AgencyPromptsPage() {
    return (
        <div className="app-content">
            <LayoutSidebar/>
            <AgencyPromptsContent/>
        </div>
    );
}

export default AgencyPromptsPage;
